(() => {
  const body = document.body;
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');

  if (menuToggle && mobileMenu) {
    const menuLinks = Array.from(mobileMenu.querySelectorAll('a'));

    const closeMenu = ({ returnFocus = false } = {}) => {
      body.classList.remove('menu-open');
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.setAttribute('aria-label', 'Open menu');
      mobileMenu.setAttribute('aria-hidden', 'true');
      if (returnFocus) menuToggle.focus();
    };

    const openMenu = () => {
      body.classList.add('menu-open');
      menuToggle.setAttribute('aria-expanded', 'true');
      menuToggle.setAttribute('aria-label', 'Close menu');
      mobileMenu.setAttribute('aria-hidden', 'false');
      menuLinks[0]?.focus();
    };

    menuToggle.addEventListener('click', () => {
      if (body.classList.contains('menu-open')) closeMenu();
      else openMenu();
    });

    menuLinks.forEach((link) => link.addEventListener('click', () => closeMenu()));

    document.addEventListener('keydown', (event) => {
      if (!body.classList.contains('menu-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu({ returnFocus: true });
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [menuToggle, ...menuLinks];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 900 && body.classList.contains('menu-open')) closeMenu();
    }, { passive: true });
  }

  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const slotRadios = Array.from(root.querySelectorAll('[data-design-slot-radio]'));
    const designSlots = Array.from(root.querySelectorAll('[data-design-slot]'));
    const primaryIds = root.querySelectorAll('[data-primary-variant-id]');
    const prices = root.querySelectorAll('[data-product-price]');
    const buttons = root.querySelectorAll('[data-add-button]');
    const quantityOptions = root.querySelectorAll('[data-bundle-quantity-option]');
    const syncedQuantities = root.querySelectorAll('[data-bundle-quantity-sync]');
    const designSummaries = root.querySelectorAll('[data-design-summary]');
    const stickyTotals = root.querySelectorAll('[data-bundle-sticky-total]');
    const mobileSelections = root.querySelectorAll('[data-mobile-selection]');
    const productForms = Array.from(root.querySelectorAll('form')).filter((form) => form.action.includes('/cart/add'));
    const featuredImage = root.querySelector('[data-featured-design-image]');

    // Products without design variants keep Shopify's native product form behavior.
    if (!slotRadios.length) return;

    const currentBundle = () => root.querySelector('[data-bundle-quantity-option]:checked');

    const currentQuantity = () => {
      const quantity = Number.parseInt(currentBundle()?.value || '1', 10);
      return Math.min(3, Math.max(1, quantity || 1));
    };

    const radiosForSlot = (slotIndex) => slotRadios.filter(
      (radio) => Number.parseInt(radio.dataset.slotIndex || '0', 10) === slotIndex
    );

    const selectedForSlot = (slotIndex) => radiosForSlot(slotIndex).find((radio) => radio.checked) || null;

    const selectDistinctDefaults = () => {
      const usedVariantIds = new Set();

      for (let slotIndex = 1; slotIndex <= 3; slotIndex += 1) {
        const available = radiosForSlot(slotIndex).filter((radio) => radio.dataset.available === 'true' && !radio.disabled);
        if (!available.length) continue;

        let selected = available.find((radio) => radio.checked);
        if (!selected || usedVariantIds.has(selected.value)) {
          selected = available.find((radio) => !usedVariantIds.has(radio.value)) || available[0];
          selected.checked = true;
        }

        usedVariantIds.add(selected.value);
      }
    };

    const setSlotVisibility = (quantity) => {
      const isSubmitting = root.dataset.submitting === 'true';
      designSlots.forEach((slot) => {
        const slotIndex = Number.parseInt(slot.dataset.slotIndex || '0', 10);
        const isActive = slotIndex > 0 && slotIndex <= quantity;
        slot.hidden = !isActive;
        slot.disabled = !isActive || isSubmitting;
        slot.setAttribute('aria-hidden', String(!isActive));
      });
    };

    const activeSelections = (quantity = currentQuantity()) => {
      const selections = [];
      for (let slotIndex = 1; slotIndex <= quantity; slotIndex += 1) {
        const selected = selectedForSlot(slotIndex);
        if (selected) selections.push(selected);
      }
      return selections;
    };

    const summarizeSelections = (selections) => {
      const groups = [];

      selections.forEach((radio) => {
        const title = radio.dataset.variantShortTitle || radio.dataset.variantTitle || 'Design';
        const existing = groups.find((group) => group.title === title);
        if (existing) existing.count += 1;
        else groups.push({ title, count: 1 });
      });

      return groups.map((group) => `${group.title}${group.count > 1 ? ` × ${group.count}` : ''}`).join(' · ');
    };

    const setMessage = (message = '') => {
      root.querySelectorAll('[data-product-form-message]').forEach((node) => {
        node.textContent = message;
        node.hidden = !message;
      });
    };

    const updateGallery = (designHandle) => {
      if (!designHandle) return;
      const gallery = root.querySelector('[data-product-gallery]');
      const target = root.querySelector(`[data-design-gallery="${CSS.escape(designHandle)}"]`);
      if (!gallery || !target) return;

      gallery.querySelectorAll('[data-design-gallery]').forEach((item) => {
        if (item === target) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      if (window.matchMedia('(max-width: 899px)').matches) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        gallery.scrollTo({
          left: Math.max(0, target.offsetLeft - gallery.offsetLeft),
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }
    };

    const renderProductState = ({ updateUrl = false } = {}) => {
      const bundle = currentBundle();
      if (!bundle || !slotRadios.length) return;

      const quantity = currentQuantity();
      setSlotVisibility(quantity);
      const selections = activeSelections(quantity);
      const primaryVariant = selections[0];
      if (!primaryVariant) return;

      const primaryVariantId = primaryVariant.value;
      const primaryTitle = primaryVariant.dataset.variantTitle || primaryVariant.dataset.variantShortTitle || 'Design';
      const designHandle = primaryVariant.dataset.variantHandle || '';
      const available = selections.length === quantity && selections.every((radio) => radio.dataset.available === 'true');
      const priceText = primaryVariant.dataset.price || '';
      const quantityLabel = quantity === 1 ? 'set' : 'sets';
      const total = bundle.dataset.bundleTotal || priceText;
      const mixSummary = summarizeSelections(selections);
      const isSubmitting = root.dataset.submitting === 'true';

      primaryIds.forEach((input) => { input.value = primaryVariantId; });
      syncedQuantities.forEach((input) => { input.value = String(quantity); });
      quantityOptions.forEach((option) => { option.disabled = isSubmitting; });
      prices.forEach((node) => { if (priceText) node.textContent = priceText; });
      designSummaries.forEach((node) => { node.textContent = `Your mix: ${mixSummary}`; });
      stickyTotals.forEach((node) => { node.textContent = total; });
      mobileSelections.forEach((node) => {
        const stickySummary = `${quantity} ${quantityLabel} · ${mixSummary}`;
        node.textContent = stickySummary;
        node.title = stickySummary;
        node.setAttribute('aria-label', stickySummary);
      });
      designSlots.forEach((slot) => {
        const slotIndex = Number.parseInt(slot.dataset.slotIndex || '0', 10);
        const selected = selectedForSlot(slotIndex);
        const output = slot.querySelector('[data-slot-current]');
        if (selected && output) output.textContent = selected.dataset.variantShortTitle || selected.dataset.variantTitle || 'Design';
      });

      if (featuredImage && primaryVariant.dataset.imageUrl) {
        featuredImage.src = primaryVariant.dataset.imageUrl;
        featuredImage.alt = primaryVariant.dataset.imageAlt || `${primaryTitle} prismatic window cling design`;
      }

      buttons.forEach((button) => {
        button.disabled = !available || isSubmitting;
        if (isSubmitting) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
        button.removeAttribute('aria-label');
        button.title = available ? `Selected: ${mixSummary}` : 'One or more selected designs are unavailable';
        const label = button.querySelector('[data-add-label]');
        if (!label) return;
        if (isSubmitting) label.textContent = 'Adding…';
        else if (!available) label.textContent = 'Choose available designs';
        else if (button.closest('.mobile-buy')) label.textContent = `Add · ${total}`;
        else label.textContent = `Add ${quantity} ${quantityLabel} · ${total}`;
      });

      root.dataset.variantId = primaryVariantId;
      root.dataset.variantIds = selections.map((radio) => radio.value).join(',');
      root.dataset.design = designHandle;
      root.dataset.bundleQuantity = String(quantity);
      updateGallery(designHandle);

      if (updateUrl && root.hasAttribute('data-main-product-page')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', primaryVariantId);
        window.history.replaceState({}, '', url);
      }
    };

    const setSubmitting = (isSubmitting) => {
      if (isSubmitting) root.dataset.submitting = 'true';
      else delete root.dataset.submitting;
      renderProductState();
    };

    const addSelectedBundle = async (event) => {
      event.preventDefault();
      if (root.dataset.submitting === 'true') return;
      const submittedForm = event.currentTarget;

      const quantity = currentQuantity();
      const selections = activeSelections(quantity);
      if (selections.length !== quantity || selections.some((radio) => radio.dataset.available !== 'true')) {
        setMessage('Choose an available design for every set.');
        return;
      }

      const quantitiesByVariant = new Map();
      selections.forEach((radio) => {
        const variantId = Number.parseInt(radio.value, 10);
        if (!Number.isFinite(variantId) || variantId <= 0) return;
        quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + 1);
      });

      const items = Array.from(quantitiesByVariant, ([id, itemQuantity]) => ({ id, quantity: itemQuantity }));
      if (!items.length || items.reduce((sum, item) => sum + item.quantity, 0) !== quantity) {
        setMessage('We could not read your design choices. Please select them again.');
        return;
      }

      setMessage();
      setSubmitting(true);

      try {
        const routesRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${routesRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'The bundle could not be added.');
        }

        window.location.assign(`${routesRoot}cart`);
      } catch (error) {
        setSubmitting(false);
        const message = error instanceof Error ? error.message : 'The bundle could not be added. Please try again.';
        setMessage(message);
        const messageNode = submittedForm.querySelector('[data-product-form-message]') || root.querySelector('[data-product-form-message]');
        messageNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    selectDistinctDefaults();

    slotRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        setMessage();
        renderProductState({ updateUrl: radio.dataset.slotIndex === '1' });
      });
    });
    quantityOptions.forEach((option) => {
      option.addEventListener('change', () => {
        setMessage();
        renderProductState();
      });
    });
    productForms.forEach((form) => form.addEventListener('submit', addSelectedBundle));

    window.addEventListener('pageshow', (event) => {
      if (event.persisted || root.dataset.submitting === 'true') {
        delete root.dataset.submitting;
        setMessage();
        renderProductState();
      }
    });

    renderProductState();
  });

  document.querySelectorAll('[data-scroll-to-buy]').forEach((button) => {
    button.addEventListener('click', () => {
      const target = document.querySelector('[data-main-product-form]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  });

  const cartPage = document.querySelector('.cart-page');

  if (cartPage) {
    let cartRequestPending = false;
    const cartError = cartPage.querySelector('[data-cart-error]');

    const setCartError = (message = '') => {
      if (!cartError) return;
      cartError.textContent = message;
      cartError.hidden = !message;
    };

    const resetCartControls = () => {
      cartRequestPending = false;
      cartPage.querySelectorAll('[data-cart-remove]').forEach((link) => {
        link.removeAttribute('aria-busy');
        link.removeAttribute('aria-disabled');
      });
    };

    cartPage.addEventListener('click', async (event) => {
      const clickedElement = event.target instanceof Element ? event.target : null;
      const removeLink = clickedElement?.closest('[data-cart-remove]');
      if (!removeLink) return;

      event.preventDefault();
      if (cartRequestPending) return;

      const variantId = removeLink.dataset.variantId || '';
      if (!/^\d+$/.test(variantId)) {
        window.location.assign(removeLink.href);
        return;
      }

      cartRequestPending = true;
      setCartError();
      removeLink.setAttribute('aria-busy', 'true');
      removeLink.setAttribute('aria-disabled', 'true');

      try {
        const routesRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${routesRoot}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'We could not remove this item.');
        }

        window.location.assign(`${routesRoot}cart`);
      } catch (error) {
        resetCartControls();
        const message = error instanceof Error ? error.message : 'We could not remove this item. Please try again.';
        setCartError(message);
        cartError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload();
        return;
      }
      resetCartControls();
      setCartError();
    });
  }

  const mobileBuy = document.querySelector('.mobile-buy');
  const mainProductPrice = document.querySelector('[data-main-product-page] .product-price');
  const siteHeader = document.querySelector('.site-header');
  const footer = document.querySelector('.site-footer');

  if (mobileBuy && mainProductPrice) {
    let quickBuyFrame = 0;

    const updateMobileBuy = () => {
      quickBuyFrame = 0;
      const priceRect = mainProductPrice.getBoundingClientRect();
      const headerRect = siteHeader?.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();
      const visibleTop = headerRect && headerRect.bottom > 0 ? headerRect.bottom : 0;
      const passedProductPrice = priceRect.bottom <= visibleTop;
      const footerIsNear = Boolean(footerRect && footerRect.top < window.innerHeight + 24);
      const visible = window.innerWidth < 900 && passedProductPrice && !footerIsNear;

      mobileBuy.classList.toggle('is-mobile-buy-visible', visible);
      mobileBuy.setAttribute('aria-hidden', String(!visible));
    };

    const requestMobileBuyUpdate = () => {
      if (!quickBuyFrame) quickBuyFrame = window.requestAnimationFrame(updateMobileBuy);
    };

    window.addEventListener('scroll', requestMobileBuyUpdate, { passive: true });
    window.addEventListener('resize', requestMobileBuyUpdate, { passive: true });
    window.addEventListener('pageshow', requestMobileBuyUpdate, { passive: true });
    updateMobileBuy();
  }

  document.documentElement.classList.remove('no-js');
})();
